import { Vector3 } from 'three';
import type HalfEdgeMesh from '@three/common/halfEdgeMesh/HalfEdgeMesh';
import { Face, Vertex } from '@thirdPartyLibraries/three-mesh-halfedge/src';

/**
 * Removes sliver triangles around the gum–tooth seam.
 *
 * The seam machinery moves vertices onto other vertices' positions (snap,
 * rim equalization) and collapses the leftovers; both can leave a vertex
 * lying almost ON an edge of a neighbouring triangle without being connected
 * to it — a fan of paper-thin triangles converging on that vertex. Visually
 * the surface looks fine; the slicer sees near-degenerate geometry.
 *
 * Two repairs, chosen by where the offending vertex projects onto the edge
 * it hugs:
 *   - projection INSIDE the edge → WELD the vertex into the edge: split the
 *     edge at the projection and collapse the new vertex into the offender.
 *     The sliver vanishes and the neighbour splits into two sound triangles.
 *   - projection at an endpoint → the face simply has a micro edge; collapse
 *     it (never removing a pinned vertex).
 *
 * `pinned` vertices (the seam polylines, borders) are never removed and
 * never moved — a weld KEEPS the offender in place, which is why it is safe
 * on the seam itself.
 */
export interface SliverCleanupStats {
  welded: number;
  collapsed: number;
  skipped: number;
}

const MIN_HEIGHT_MM = 0.01;
const MAX_HEIGHT_MM = 0.06;
const HEIGHT_RATIO = 0.12;

export function cleanupSliversAround(
  mesh: HalfEdgeMesh,
  around: Iterable<Vertex>,
  pinned: Set<Vertex>,
  tag: string,
  passes = 3,
): SliverCleanupStats {
  const stats: SliverCleanupStats = { welded: 0, collapsed: 0, skipped: 0 };

  // Working region: the seam vertices plus one ring — the offending vertex
  // often sits one ring off the seam.
  const region = new Set<Vertex>();
  for (const v of around) {
    if (!isAlive(v)) continue;
    region.add(v);
    try {
      for (const n of v.adjacentVertices()) region.add(n);
    } catch {
      /* half-collapsed neighbourhood — the face scan below still guards */
    }
  }

  for (let pass = 0; pass < passes; pass++) {
    const faces = collectIncidentFaces(region);
    let acted = 0;
    for (const face of faces) {
      const sliver = detectSliver(face);
      if (!sliver) continue;
      if (repairSliver(mesh, sliver, pinned, stats, tag)) acted++;
    }
    if (acted === 0) break;
  }
  return stats;
}

interface Sliver {
  face: Face;
  /** The vertex hugging the opposite edge. */
  offender: Vertex;
  /** The hugged edge's endpoints (a face edge of `face`). */
  edgeA: Vertex;
  edgeB: Vertex;
  /** Parameter of the offender's projection along edgeA→edgeB. */
  t: number;
}

function isAlive(v: Vertex): boolean {
  return !!v.halfedge && v.halfedge.vertex === v;
}

function faceAlive(face: Face): boolean {
  return !!face.halfedge && face.halfedge.face === face;
}

function faceVerts(face: Face): Vertex[] | null {
  if (!faceAlive(face)) return null;
  const verts: Vertex[] = [];
  let guard = 0;
  for (const he of face.halfedge.nextLoop()) {
    verts.push(he.vertex);
    if (guard++ > 4) return null; // not a triangle any more
  }
  return verts.length === 3 ? verts : null;
}

function collectIncidentFaces(region: Set<Vertex>): Set<Face> {
  const faces = new Set<Face>();
  for (const v of region) {
    if (!isAlive(v)) continue;
    try {
      for (const he of v.loopCW()) {
        if (he.face) faces.add(he.face);
      }
    } catch {
      /* stale connectivity — skip */
    }
  }
  return faces;
}

const _edge = new Vector3();
const _toOffender = new Vector3();
const _foot = new Vector3();

function detectSliver(face: Face): Sliver | null {
  const verts = faceVerts(face);
  if (!verts) return null;
  const [v0, v1, v2] = verts;
  let longest = 0;
  for (const [a, b] of [
    [v0, v1],
    [v1, v2],
    [v2, v0],
  ] as const) {
    longest = Math.max(longest, a.position.distanceTo(b.position));
  }
  const threshold = Math.min(MAX_HEIGHT_MM, Math.max(MIN_HEIGHT_MM, HEIGHT_RATIO * longest));

  let best: Sliver | null = null;
  let bestHeight = threshold;
  for (let i = 0; i < 3; i++) {
    const offender = verts[i];
    const edgeA = verts[(i + 1) % 3];
    const edgeB = verts[(i + 2) % 3];
    _edge.subVectors(edgeB.position, edgeA.position);
    const lengthSq = _edge.lengthSq();
    if (lengthSq < 1e-12) continue;
    const t = _toOffender.subVectors(offender.position, edgeA.position).dot(_edge) / lengthSq;
    _foot.copy(edgeA.position).addScaledVector(_edge, Math.min(1, Math.max(0, t)));
    const height = _foot.distanceTo(offender.position);
    if (height < bestHeight) {
      bestHeight = height;
      best = { face, offender, edgeA, edgeB, t };
    }
  }
  return best;
}

function repairSliver(
  mesh: HalfEdgeMesh,
  sliver: Sliver,
  pinned: Set<Vertex>,
  stats: SliverCleanupStats,
  tag: string,
): boolean {
  const { offender, edgeA, edgeB, t } = sliver;

  if (t > 0.1 && t < 0.9) {
    // WELD: split the hugged edge at the projection, merge the split vertex
    // into the offender. Boundary edges are seam polylines — never re-route
    // them through a surface vertex.
    const halfedge = edgeA.getHalfedgeToVertex(edgeB);
    if (!halfedge || !halfedge.face || !halfedge.twin.face) {
      stats.skipped++;
      return false;
    }
    const f1 = halfedge.face;
    const f2 = halfedge.twin.face;
    _edge.subVectors(edgeB.position, edgeA.position);
    const projection = new Vector3().copy(edgeA.position).addScaledVector(_edge, t);
    try {
      const w = mesh.halfEdges.splitEdge(halfedge, projection, 1e-6);
      if (w === edgeA || w === edgeB) {
        stats.skipped++;
        return false;
      }
      for (const quad of [f1, f2]) {
        const corner = quadCorner(quad, edgeA, edgeB, w);
        if (corner) mesh.halfEdges.cutFace(quad, w, corner, true);
      }
      if (mesh.halfEdges.edgeCollapse(w, offender, false)) {
        stats.welded++;
        return true;
      }
      console.warn(`cleanupSliversAround [${tag}]: weld collapse refused (vertex ${offender.id})`);
      stats.skipped++;
      return false;
    } catch (e) {
      console.warn(`cleanupSliversAround [${tag}]: weld failed`, e);
      stats.skipped++;
      return false;
    }
  }

  // Micro edge: the offender projects onto an endpoint — merge the pair,
  // keeping the pinned one (both pinned → the seam itself is degenerate
  // there, which is a polyline shape, not a printability defect — leave it).
  const endpoint = t < 0.5 ? edgeA : edgeB;
  let source: Vertex;
  let target: Vertex;
  if (pinned.has(offender) && pinned.has(endpoint)) {
    stats.skipped++;
    return false;
  } else if (pinned.has(offender)) {
    source = endpoint;
    target = offender;
  } else {
    source = offender;
    target = endpoint;
  }
  if (mesh.halfEdges.edgeCollapse(source, target, false)) {
    stats.collapsed++;
    return true;
  }
  stats.skipped++;
  return false;
}

function quadCorner(face: Face, a: Vertex, b: Vertex, w: Vertex): Vertex | null {
  if (!faceAlive(face)) return null;
  let corner: Vertex | null = null;
  let guard = 0;
  for (const he of face.halfedge.nextLoop()) {
    if (he.vertex !== a && he.vertex !== b && he.vertex !== w) corner = he.vertex;
    if (guard++ > 5) return null;
  }
  return corner;
}
